import { Box } from '@material-ui/core';
import { useEffect, useState } from 'react';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { NextActionAccordion } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/NextActions/components/_shared/NextActionAccordion';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { BnplPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/entities/PaymentMethodDetail/BnplPaymentMethodDetailEntity';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { CustomAlert } from '@/components/shared/components/CustomAlert';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useUser } from '@/hooks/useUser';

interface NextActionsProps<T extends PaymentMethodDetailEntity> {
  paymentMethodDetailEntity: T;
  NextActionFactory: React.ComponentType<{
    action: Action;
    paymentMethodDetailEntity: T;
  }>;
  ActionButtons?: React.ReactNode;
}

export const NextActions = <T extends PaymentMethodDetailEntity>({
  paymentMethodDetailEntity,
  NextActionFactory,
  ActionButtons,
}: NextActionsProps<T>) => {
  const { checkoutSessionEntity } = useCheckoutSessionContext();
  const { isUserAdmin, isUserAgent, isUserMerchant, isUserChannel } = useUser();

  const actions = paymentMethodDetailEntity.actions;
  const isUserAllowedToEditVehicleData = isUserAdmin || isUserAgent || isUserMerchant || isUserChannel;

  const [completedActions, setCompletedActions] = useState<Action[] | null>(null);

  useEffect(() => {
    getCompletedActions().then(setCompletedActions);
  }, [actions, paymentMethodDetailEntity]);

  const getCompletedActions = async () => {
    if (!actions || actions.length === 0) {
      return null;
    }
    const completedActions = await Promise.all(
      actions.map(async (action) => {
        return { action, isCompleted: await paymentMethodDetailEntity.isActionCompleted(action) };
      }),
    );

    return completedActions.filter((action) => action.isCompleted === true).map((action) => action.action);
  };

  if (
    !checkoutSessionEntity ||
    !completedActions ||
    !actions ||
    actions.length === 0 ||
    paymentMethodDetailEntity.isDeniedOrCancelledOrExpired()
  ) {
    return null;
  }

  const getIsFinalStatus = (): boolean => {
    if (paymentMethodDetailEntity instanceof BnplPaymentMethodDetailEntity) {
      return paymentMethodDetailEntity.isFinalStatus();
    }
    return checkoutSessionEntity.hasFinalStatus();
  };

  const isFinalStatus = getIsFinalStatus();
  const firstUncompletedAction = actions.find((action) => !completedActions.includes(action));

  const retentionAlert = getRetentionAlert(
    paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity ? paymentMethodDetailEntity : null,
  );

  const missingAutoDetailsRetentionAlert = getMissingAutoDetailsRetentionAlert(
    paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity ? paymentMethodDetailEntity : null,
    !!isUserAllowedToEditVehicleData,
  );

  return (
    <CustomPaper fill="paper">
      <FlexRow>
        <Font variant="subtitle1" color="title">
          <Text strong tId={'NEXT_STEPS'} />
        </Font>
        {ActionButtons}
      </FlexRow>
      <Box mt={2} />
      {retentionAlert && (
        <Box mb={2}>
          <CustomAlert
            severity={retentionAlert.severity}
            title={retentionAlert.titleId}
            style={{ padding: '4px 12px' }}
          />
        </Box>
      )}
      {missingAutoDetailsRetentionAlert && (
        <Box mb={2}>
          <CustomAlert
            severity={missingAutoDetailsRetentionAlert.severity}
            title={missingAutoDetailsRetentionAlert.titleId}
            style={{ padding: '4px 12px' }}
          />
        </Box>
      )}
      {actions.map((action, i) => {
        const isCompleted = completedActions.includes(action);

        let failed = false;
        if (paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity) {
          const loanApplication = paymentMethodDetailEntity.loan?.loanApplicationSelected;
          if (action === Action.COMPLETE_KYC) {
            failed = paymentMethodDetailEntity.hasSomeKycFailed();
          } else if (action === Action.SIGN_CONTRACT) {
            failed =
              !!loanApplication?.contractRequiresResignature ||
              !!(
                paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity &&
                paymentMethodDetailEntity.isMissingVehicleRegistrationNumberOrLicensePlate() &&
                paymentMethodDetailEntity.modificationCapability?.modifiable &&
                !paymentMethodDetailEntity.isSigned()
              );
          } else if (action === Action.UPLOAD_DOCUMENTS) {
            failed = !!loanApplication?.isDocumentationWithIncidence;
          }
        }

        const expanded = shouldActionAccordionBeExpanded({
          isCheckoutSessionInFinalStatus: isFinalStatus,
          isActionCompleted: isCompleted,
          isActionFailed: failed,
          isFirstUnCompletedAction: firstUncompletedAction === action,
        });

        return (
          <Box key={`next-action-${action}`}>
            <NextActionAccordion
              action={action}
              stepNumber={i + 1}
              disabled={isFinalStatus}
              completed={isCompleted}
              expanded={expanded}
              failed={failed}
            >
              <NextActionFactory action={action} paymentMethodDetailEntity={paymentMethodDetailEntity} />
            </NextActionAccordion>
            {i < actions.length - 1 && <CustomDivider />}
          </Box>
        );
      })}
    </CustomPaper>
  );
};

const shouldActionAccordionBeExpanded = ({
  isCheckoutSessionInFinalStatus,
  isActionCompleted,
  isActionFailed,
  isFirstUnCompletedAction,
}: {
  isCheckoutSessionInFinalStatus: boolean;
  isActionCompleted: boolean;
  isActionFailed: boolean;
  isFirstUnCompletedAction: boolean;
}) => {
  if (isCheckoutSessionInFinalStatus || isActionCompleted) {
    return false;
  }

  if (isActionFailed) {
    return true;
  }

  return isFirstUnCompletedAction;
};

const getRetentionAlert = (paymentMethodDetailEntity: PaymentMethodDetailEntity | null | undefined) => {
  if (!paymentMethodDetailEntity) return null;

  const loanApplication =
    paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity
      ? paymentMethodDetailEntity.loan?.loanApplicationSelected
      : null;

  if (loanApplication) {
    if (loanApplication.requiresInternalReview)
      return { severity: 'info' as const, titleId: 'LOAN_RETENTION_INTERNAL_REVIEW' };

    if (loanApplication.contractRequiresResignature)
      return { severity: 'warning' as const, titleId: 'LOAN_RETENTION_CONTRACT_RESIGNATURE' };

    if (loanApplication.isDocumentationWithIncidence)
      return { severity: 'warning' as const, titleId: 'LOAN_RETENTION_DOCS_INCIDENCE' };
  }
};

const getMissingAutoDetailsRetentionAlert = (
  loanEntity: LoanPaymentMethodDetailEntity | null,
  isUserAllowedToEditVehicleData: boolean,
) => {
  if (!loanEntity) return null;

  const capability = loanEntity.modificationCapability;
  const isCapabilityReady = !!capability;

  const showMissingVehicleDataWarning =
    !!loanEntity &&
    isUserAllowedToEditVehicleData &&
    isCapabilityReady &&
    loanEntity.isMissingVehicleRegistrationNumberOrLicensePlate() &&
    capability.modifiable &&
    !loanEntity.isSigned();

  if (showMissingVehicleDataWarning) {
    const titleId = getMissingVehicleIdentificationWarningTitleId(loanEntity);

    if (titleId) {
      return { severity: 'warning' as const, titleId };
    }
  }

  return null;
};

const getMissingVehicleIdentificationWarningTitleId = (
  loanEntity: LoanPaymentMethodDetailEntity,
): string | undefined => {
  const isMissingRegistrationNumber = loanEntity.isMissingVehicleRegistrationNumber();
  const isMissingLicensePlate = loanEntity.isMissingVehicleLicensePlate();
  const lenderAllowsModification = loanEntity.modificationCapability?.lenderAllowsModification;

  if (isMissingRegistrationNumber && isMissingLicensePlate) {
    if (lenderAllowsModification) {
      return 'MISSING_VEHICLE_DATA_WARNING_EDIT_IN_PLATFORM';
    }
    return 'MISSING_VEHICLE_DATA_WARNING_EDIT_IN_LENDER';
  }

  if (isMissingRegistrationNumber) {
    if (lenderAllowsModification) {
      return 'MISSING_VEHICLE_REGISTRATION_NUMBER_WARNING_EDIT_IN_PLATFORM';
    }
    return 'MISSING_VEHICLE_REGISTRATION_NUMBER_WARNING_EDIT_IN_LENDER';
  }

  if (isMissingLicensePlate) {
    if (lenderAllowsModification) {
      return 'MISSING_VEHICLE_LICENSE_PLATE_WARNING_EDIT_IN_PLATFORM';
    }
    return 'MISSING_VEHICLE_LICENSE_PLATE_WARNING_EDIT_IN_LENDER';
  }

  return undefined;
};
